import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  MapPin, Plus, Trash2, ArrowLeft, Camera, ExternalLink,
  BadgeCheck, Clock3, XCircle, Phone, Upload, Loader2, X, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import AuthedImage from "@/components/AuthedImage";

const DECISIONS = [
  { key: "shortlisted", label: "Shortlisted", color: "#F5B82E", Icon: Clock3 },
  { key: "finalized",   label: "Finalized",   color: "#22C55E", Icon: BadgeCheck },
  { key: "rejected",    label: "Rejected",    color: "#EF4444", Icon: XCircle },
];

const PERMITS = [
  { key: "none",    label: "No permit yet" },
  { key: "applied", label: "Applied" },
  { key: "granted", label: "Granted" },
];

export default function ProjectRecce() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject]   = useState(null);
  const [rows, setRows]         = useState([]);
  const [counts, setCounts]     = useState({shortlisted:0, finalized:0, rejected:0});
  const [openAdd, setOpenAdd]   = useState(false);
  const [view, setView]         = useState("kanban");   // kanban | list
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState(blankForm());

  function blankForm() {
    return {
      name: "", address: "", city: "",
      permit_status: "none",
      contact_name: "", contact_phone: "",
      notes: "", decision: "shortlisted",
    };
  }

  async function load() {
    try {
      const [{ data: p }, { data: r }] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/locations`),
      ]);
      setProject(p.project || p);
      setRows(r.locations || []);
      setCounts(r.counts || {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load recce");
    }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [id]);

  async function add(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${id}/locations`, {
        name: form.name, address: form.address, city: form.city,
        permit_status: form.permit_status,
        contact_name: form.contact_name, contact_phone: form.contact_phone,
        notes: form.notes, decision: form.decision,
      });
      toast.success("Location added — you can now upload photos from the card");
      setOpenAdd(false);
      setForm(blankForm());
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Add failed");
    } finally { setSaving(false); }
  }

  async function uploadPhoto(locId, file) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo must be under 5 MB"); return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(
        `/projects/${id}/locations/${locId}/photos`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 },
      );
      toast.success("Photo uploaded");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    }
  }

  async function removePhoto(locId, photo) {
    try {
      await api.delete(`/projects/${id}/locations/${locId}/photos`, {
        params: { photo },
      });
      toast.success("Photo removed");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Remove failed");
    }
  }

  async function move(rowId, decision) {
    try {
      await api.patch(`/projects/${id}/locations/${rowId}`, { decision });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Move failed");
    }
  }
  async function remove(rowId) {
    if (!window.confirm("Remove this location from the recce list?")) return;
    try {
      await api.delete(`/projects/${id}/locations/${rowId}`);
      toast.success("Removed");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Remove failed");
    }
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8" data-testid="project-recce-page">
      <button
        onClick={() => nav(`/p/${id}`)}
        className="text-[10px] tracking-[0.25em] uppercase text-white/55 hover:text-white inline-flex items-center gap-1.5"
        data-testid="back-to-project">
        <ArrowLeft size={12} /> Back to Project
      </button>

      <div className="mt-3 flex items-start justify-between gap-6 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Pre-Production · Recce</div>
          <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight" data-testid="recce-title">
            {project?.title || "Location Scouting"}
          </h1>
          <div className="mt-2 text-sm text-white/55">
            {rows.length} scouted · {counts.finalized || 0} finalized · {counts.shortlisted || 0} in review
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full border border-white/10 p-1">
            {["kanban", "list"].map(v => (
              <button key={v} onClick={() => setView(v)}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] rounded-full ${view===v ? "bg-white/15 text-white" : "text-white/50"}`}
                data-testid={`recce-view-${v}`}>
                {v}
              </button>
            ))}
          </div>
          <Button onClick={() => setOpenAdd(true)} className="btn-themed rounded-full uppercase tracking-[0.22em] text-xs h-9 px-4"
            data-testid="recce-add-btn">
            <Plus size={14} className="mr-1.5" /> Add Location
          </Button>
        </div>
      </div>

      {rows.length === 0 && (
        <div className="mt-8 cs-card rounded-2xl p-10 text-center" data-testid="recce-empty">
          <MapPin size={28} className="mx-auto text-white/40" />
          <div className="mt-3 text-sm text-white/60">No locations scouted yet.</div>
          <div className="mt-1 text-xs text-white/45">Add every recce location so your team can share notes, photos and permits in one place.</div>
        </div>
      )}

      {rows.length > 0 && view === "kanban" && (
        <div className="mt-6 grid md:grid-cols-3 gap-4" data-testid="recce-kanban">
          {DECISIONS.map(d => {
            const list = rows.filter(r => (r.decision || "shortlisted") === d.key);
            return (
              <div key={d.key} className="cs-card rounded-2xl p-4"
                style={{ borderColor: `${d.color}22` }}
                data-testid={`recce-col-${d.key}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <d.Icon size={14} style={{ color: d.color }} />
                    <div className="text-[10px] tracking-[0.28em] uppercase font-semibold" style={{ color: d.color }}>
                      {d.label}
                    </div>
                  </div>
                  <div className="text-lg font-display tabular-nums" style={{ color: d.color }}>{list.length}</div>
                </div>
                <div className="space-y-2">
                  {list.map(l => (
                    <LocationCard key={l.id} loc={l} projectId={id}
                      onMove={move} onRemove={remove}
                      onUploadPhoto={uploadPhoto}
                      onRemovePhoto={removePhoto}
                      decisionColor={d.color} />
                  ))}
                  {list.length === 0 && (
                    <div className="text-center text-[11px] text-white/35 py-6">Empty</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {rows.length > 0 && view === "list" && (
        <div className="mt-6 cs-card rounded-2xl p-6" data-testid="recce-list">
          <div className="space-y-2">
            {rows.map(l => (
              <LocationRow key={l.id} loc={l} onMove={move} onRemove={remove} />
            ))}
          </div>
        </div>
      )}

      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent className="bg-[#0c0c0e] border-white/10 sm:max-w-2xl" data-testid="recce-add-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">Add scouted location</DialogTitle>
            <DialogDescription className="text-white/55">Paste photo URLs from Drive/Dropbox one per line for now.</DialogDescription>
          </DialogHeader>
          <form onSubmit={add} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Location Name *">
                <Input required value={form.name} data-testid="recce-name"
                  onChange={e => setForm({...form, name: e.target.value})}
                  className="bg-black/40 border-white/15" />
              </Field>
              <Field label="City">
                <Input value={form.city} data-testid="recce-city"
                  onChange={e => setForm({...form, city: e.target.value})}
                  className="bg-black/40 border-white/15" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Address">
                  <Input value={form.address} data-testid="recce-address"
                    onChange={e => setForm({...form, address: e.target.value})}
                    className="bg-black/40 border-white/15" />
                </Field>
              </div>
              <Field label="Permit Status">
                <Select value={form.permit_status} onValueChange={(v)=>setForm({...form, permit_status: v})}>
                  <SelectTrigger className="bg-black/40 border-white/15" data-testid="recce-permit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PERMITS.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Decision">
                <Select value={form.decision} onValueChange={(v)=>setForm({...form, decision: v})}>
                  <SelectTrigger className="bg-black/40 border-white/15" data-testid="recce-decision"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DECISIONS.map(d => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Contact Name">
                <Input value={form.contact_name} data-testid="recce-contact-name"
                  onChange={e => setForm({...form, contact_name: e.target.value})}
                  className="bg-black/40 border-white/15" />
              </Field>
              <Field label="Contact Phone">
                <Input value={form.contact_phone} data-testid="recce-contact-phone"
                  onChange={e => setForm({...form, contact_phone: e.target.value})}
                  className="bg-black/40 border-white/15" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Notes">
                  <Textarea value={form.notes} data-testid="recce-notes"
                    onChange={e => setForm({...form, notes: e.target.value})}
                    className="bg-black/40 border-white/15 min-h-[80px]" />
                </Field>
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="ghost" onClick={()=>setOpenAdd(false)} data-testid="recce-cancel">Cancel</Button>
              <Button type="submit" disabled={saving} className="btn-themed rounded-full uppercase tracking-[0.22em] text-xs px-5"
                data-testid="recce-submit">
                {saving ? "Saving…" : "Save location"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LocationCard({ loc, projectId, onMove, onRemove, onUploadPhoto, onRemovePhoto, decisionColor }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const permitTone = loc.permit_status === "granted" ? "text-emerald-300"
    : loc.permit_status === "applied" ? "text-amber-300"
    : "text-white/45";
  const photos = loc.photo_urls || [];

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try { await onUploadPhoto(loc.id, file); }
    finally { setUploading(false); }
  }

  return (
    <div className="rounded-xl p-3 border border-white/10 bg-black/30 hover:border-white/25 transition"
      data-testid={`recce-card-${loc.id}`}
      style={{ borderLeft: `3px solid ${decisionColor}` }}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate" data-testid={`recce-card-name-${loc.id}`}>{loc.name}</div>
          <div className="text-[11px] text-white/50 truncate">{loc.city || loc.address || "—"}</div>
        </div>
        <button onClick={() => onRemove(loc.id)} className="p-1 text-white/35 hover:text-red-400"
          data-testid={`recce-remove-${loc.id}`}>
          <Trash2 size={12} />
        </button>
      </div>

      {photos.length > 0 && (
        <div className="mt-2 grid grid-cols-3 gap-1" data-testid={`recce-photos-${loc.id}`}>
          {photos.slice(0, 5).map((p, idx) => (
            <div key={`${loc.id}-p-${idx}`} className="relative group aspect-square rounded overflow-hidden">
              {p.startsWith("emergent:") ? (
                <AuthedImage
                  photo={p} projectId={projectId} locationId={loc.id}
                  className="w-full h-full rounded"
                  testid={`recce-thumb-${loc.id}-${idx}`}
                />
              ) : (
                <a href={p} target="_blank" rel="noreferrer" className="block w-full h-full">
                  <div className="w-full h-full flex items-center justify-center bg-black/60 border border-white/10 rounded">
                    <ExternalLink size={11} className="text-white/60" />
                  </div>
                </a>
              )}
              <button
                onClick={() => onRemovePhoto(loc.id, p)}
                className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-black/70 opacity-0 group-hover:opacity-100 transition text-white/85 hover:text-red-400"
                title="Remove photo"
                data-testid={`recce-photo-remove-${loc.id}-${idx}`}>
                <X size={9} />
              </button>
            </div>
          ))}
          {photos.length > 5 && (
            <div className="aspect-square rounded bg-black/50 border border-white/10 flex items-center justify-center text-[10px] text-white/60">
              +{photos.length - 5}
            </div>
          )}
        </div>
      )}

      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <span className={`text-[9px] uppercase tracking-[0.18em] ${permitTone}`}>
          Permit · {loc.permit_status}
        </span>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading || photos.length >= 20}
          className="text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-white/15 hover:border-fuchsia-400/60 hover:text-fuchsia-300 inline-flex items-center gap-1 disabled:opacity-40"
          data-testid={`recce-photo-add-${loc.id}`}>
          {uploading
            ? (<><Loader2 size={9} className="animate-spin" /> Uploading</>)
            : (<><Upload size={9} /> {photos.length === 0 ? "Add photos" : `${photos.length}/20`}</>)}
        </button>
        <input
          ref={fileRef} type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden" onChange={handleFile}
          data-testid={`recce-photo-input-${loc.id}`} />
      </div>

      {loc.contact_name && (
        <div className="mt-2 text-[10px] text-white/55 flex items-center gap-1.5">
          <Phone size={9} /> {loc.contact_name} {loc.contact_phone ? `· ${loc.contact_phone}` : ""}
        </div>
      )}
      {loc.notes && (
        <div className="mt-2 text-[11px] text-white/60 line-clamp-2">{loc.notes}</div>
      )}
      <div className="mt-3 flex gap-1.5">
        {DECISIONS.filter(d => d.key !== (loc.decision || "shortlisted")).map(d => (
          <button key={d.key}
            onClick={() => onMove(loc.id, d.key)}
            className="text-[9px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border border-white/10 hover:border-white/40"
            style={{ color: d.color }}
            data-testid={`recce-move-${loc.id}-${d.key}`}>
            → {d.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function LocationRow({ loc, onMove, onRemove }) {
  const cur = DECISIONS.find(d => d.key === (loc.decision || "shortlisted"));
  return (
    <div className="grid grid-cols-12 gap-3 items-center py-3 px-3 rounded-xl border border-white/5 hover:border-white/15"
      data-testid={`recce-row-${loc.id}`}>
      <div className="col-span-4 min-w-0">
        <div className="font-medium truncate">{loc.name}</div>
        <div className="text-xs text-white/55 truncate">{loc.address || loc.city || "—"}</div>
      </div>
      <div className="col-span-2 text-xs text-white/60 truncate">
        {loc.contact_name || "—"}
      </div>
      <div className="col-span-2 text-[10px] uppercase tracking-[0.2em] text-white/55">
        Permit · {loc.permit_status}
      </div>
      <div className="col-span-3">
        <div className="flex gap-1.5">
          {DECISIONS.map(d => (
            <button key={d.key} onClick={() => onMove(loc.id, d.key)}
              className={`text-[9px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border ${d.key === cur?.key ? "border-white/40" : "border-white/10 hover:border-white/25"}`}
              style={{ color: d.color }}
              data-testid={`recce-list-move-${loc.id}-${d.key}`}>
              {d.label}
            </button>
          ))}
        </div>
      </div>
      <div className="col-span-1 text-right">
        <button onClick={() => onRemove(loc.id)} className="p-2 text-white/40 hover:text-red-400"
          data-testid={`recce-list-remove-${loc.id}`}>
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
